/**
 * Pipedrive API Hooks
 * React Query hooks for fetching Pipedrive data
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  PipedriveOrganization,
  PipedriveOrganizationDetail,
  PipedriveOrganizationParams,
  PipedriveOrganizationFilterOptions,
  PipedrivePerson,
  PipedrivePersonDetail,
  PipedrivePeopleParams,
  PipedriveDeal,
  PipedriveDealDetail,
  PipedriveDealParams,
  PipedriveDealFilterOptions,
  PipedriveActivity,
  PipedriveActivityParams,
  PipedriveActivityFilterOptions,
  PipedriveNote,
  PipedriveNoteParams,
  PipedriveLead,
  PipedriveLeadParams,
  PipedriveProduct,
  PipedriveProductParams,
  PipedriveProductFilterOptions,
  PipedriveFile,
  PipedriveListParams,
  PipedriveListResponse,
  PipedriveStatsResponse,
  PipedriveSearchResponse,
} from '@/types/pipedrive'

const PIPEDRIVE_BASE = '/pipedrive'

// Stats Hook
export function usePipedriveStats() {
  return useQuery({
    queryKey: ['pipedrive', 'stats'],
    queryFn: async (): Promise<PipedriveStatsResponse> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/stats`)
      return response.data
    },
  })
}

// Search Hook
export function usePipedriveSearch(query: string) {
  return useQuery({
    queryKey: ['pipedrive', 'search', query],
    queryFn: async (): Promise<PipedriveSearchResponse> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/search`, {
        params: { q: query },
      })
      return response.data
    },
    enabled: query.length >= 2,
  })
}

// Organizations Hooks
export function usePipedriveOrganizations(params?: PipedriveOrganizationParams) {
  return useQuery({
    queryKey: ['pipedrive', 'organizations', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedriveOrganization>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/organizations`, { params })
      return response.data
    },
  })
}

export function usePipedriveOrganization(id: number | string) {
  return useQuery({
    queryKey: ['pipedrive', 'organizations', id],
    queryFn: async (): Promise<PipedriveOrganizationDetail> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/organizations/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function usePipedriveOrganizationFilters() {
  return useQuery({
    queryKey: ['pipedrive', 'organizations', 'filters'],
    queryFn: async (): Promise<PipedriveOrganizationFilterOptions> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/organizations/filters/options`)
      return response.data
    },
  })
}

// People Hooks
export function usePipedrivePeople(params?: PipedrivePeopleParams) {
  return useQuery({
    queryKey: ['pipedrive', 'people', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedrivePerson>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/people`, { params })
      return response.data
    },
  })
}

export function usePipedrivePerson(id: number | string) {
  return useQuery({
    queryKey: ['pipedrive', 'people', id],
    queryFn: async (): Promise<PipedrivePersonDetail> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/people/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

// Deals Hooks
export function usePipedriveDeals(params?: PipedriveDealParams) {
  return useQuery({
    queryKey: ['pipedrive', 'deals', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedriveDeal>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/deals`, { params })
      return response.data
    },
  })
}

export function usePipedriveDeal(id: number | string) {
  return useQuery({
    queryKey: ['pipedrive', 'deals', id],
    queryFn: async (): Promise<PipedriveDealDetail> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/deals/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function usePipedriveDealFilters() {
  return useQuery({
    queryKey: ['pipedrive', 'deals', 'filters'],
    queryFn: async (): Promise<PipedriveDealFilterOptions> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/deals/filters/options`)
      return response.data
    },
  })
}

// Activities Hooks
export function usePipedriveActivities(params?: PipedriveActivityParams) {
  return useQuery({
    queryKey: ['pipedrive', 'activities', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedriveActivity>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/activities`, { params })
      return response.data
    },
  })
}

export function usePipedriveActivity(id: number | string) {
  return useQuery({
    queryKey: ['pipedrive', 'activities', id],
    queryFn: async (): Promise<PipedriveActivity> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/activities/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function usePipedriveActivityFilters() {
  return useQuery({
    queryKey: ['pipedrive', 'activities', 'filters'],
    queryFn: async (): Promise<PipedriveActivityFilterOptions> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/activities/filters/options`)
      return response.data
    },
  })
}

// Notes Hooks
export function usePipedriveNotes(params?: PipedriveNoteParams) {
  return useQuery({
    queryKey: ['pipedrive', 'notes', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedriveNote>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/notes`, { params })
      return response.data
    },
  })
}

export function usePipedriveNote(id: number | string) {
  return useQuery({
    queryKey: ['pipedrive', 'notes', id],
    queryFn: async (): Promise<PipedriveNote> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/notes/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

// Leads Hooks
export function usePipedriveLeads(params?: PipedriveLeadParams) {
  return useQuery({
    queryKey: ['pipedrive', 'leads', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedriveLead>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/leads`, { params })
      return response.data
    },
  })
}

export function usePipedriveLead(id: string) {
  return useQuery({
    queryKey: ['pipedrive', 'leads', id],
    queryFn: async (): Promise<PipedriveLead> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/leads/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

// Products Hooks
export function usePipedriveProducts(params?: PipedriveProductParams) {
  return useQuery({
    queryKey: ['pipedrive', 'products', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedriveProduct>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/products`, { params })
      return response.data
    },
  })
}

export function usePipedriveProduct(id: number | string) {
  return useQuery({
    queryKey: ['pipedrive', 'products', id],
    queryFn: async (): Promise<PipedriveProduct> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/products/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

export function usePipedriveProductFilters() {
  return useQuery({
    queryKey: ['pipedrive', 'products', 'filters'],
    queryFn: async (): Promise<PipedriveProductFilterOptions> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/products/filters/options`)
      return response.data
    },
  })
}

// Files Hooks
export function usePipedriveFiles(params?: PipedriveListParams) {
  return useQuery({
    queryKey: ['pipedrive', 'files', params],
    queryFn: async (): Promise<PipedriveListResponse<PipedriveFile>> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/files`, { params })
      return response.data
    },
  })
}

export function usePipedriveFile(id: number | string) {
  return useQuery({
    queryKey: ['pipedrive', 'files', id],
    queryFn: async (): Promise<PipedriveFile> => {
      const response = await api.get(`${PIPEDRIVE_BASE}/files/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}
